import {
  faGiftCard,
  faLongArrowUp,
  faMoneyBillWave,
  faUmbrellaBeach,
  IconDefinition
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Log, LogType } from './types';

import { formatISOString } from 'utils/date';
import { displayFlooredNumber } from 'utils/misc';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

import { FontAwesomeSvgIcon } from 'components/shared';

const StyledLog = styled(Paper)`
  padding: 20px;
  margin-bottom: 20px;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }

  > p:first-child {
    margin-bottom: 20px;
  }

  svg {
    margin-right: 20px;
  }

  p {
    margin-bottom: 0;
    font-family: 'signikaregular';
  }

  .hours {
    position: relative;
    margin-left: 20px;

    .text {
      position: absolute;
      right: 0;
      top: 0;
      transform: translateY(-100%);
    }

    .total {
      font-size: 16px;
    }
  }

  .credit-button {
    position: absolute;
    top: 2px;
    right: 2px;
  }
`;

const icons: { [T in LogType]: IconDefinition } = {
  EXTRA_STATUTORY_LEAVE: faUmbrellaBeach,
  PLB: faUmbrellaBeach,
  PLUS_HOURS: faLongArrowUp,
  MANUAL: faMoneyBillWave,
  SALARY_EXPORT: faMoneyBillWave,
  WITHDRAWAL: faUmbrellaBeach,
  GIFT: faGiftCard
};

type Props = {
  log: Log;
  isEditMode: boolean;
  onCredit: (log: Log) => void;
};

export default function LogEntry(props: Props) {
  const { log, isEditMode, onCredit } = props;
  const { type, createdOn, createdBy, hours, canBeCredited, name } = log;
  const { t } = useTranslation('common');
  const descriptions: { [T in LogType]: string } = {
    EXTRA_STATUTORY_LEAVE: t('balanceBudget.logSource1'),
    PLB: name,
    PLUS_HOURS: t('balanceBudget.logSource2'),
    MANUAL: t('balanceBudget.logSource3'),
    SALARY_EXPORT: t('balanceBudget.logSource4'),
    WITHDRAWAL: t('balanceBudget.logSource5'),
    GIFT: t('balanceBudget.logSource6')
  };

  return (
    <StyledLog className="log">
      <p>
        {createdBy
          ? t('balanceBudget.logProcessedInfo', {
              date: formatISOString(createdOn, 'dd MMMM yyyy'),
              name: createdBy
            })
          : formatISOString(createdOn, 'dd MMMM yyyy')}
      </p>
      <div className="d-flex between">
        <FontAwesomeSvgIcon icon={icons[type]} />
        <p className="grow-1">{descriptions[type]}</p>
        {isEditMode && canBeCredited && (
          <Button
            variant="outlined"
            size="small"
            className="credit-button"
            onClick={() => {
              onCredit(log);
            }}
          >
            {t('balanceBudget.credit')}
          </Button>
        )}
        <div className="hours">
          <div className="text">{t('hours')}</div>
          <div className="total semibold">{displayFlooredNumber(hours)}</div>
        </div>
      </div>
    </StyledLog>
  );
}
